import { inject, injectable } from 'inversify-props';
import { IHttpClient, ILogger, ISettings } from '../common';
import { DeckImage, PagedResult, DeckColor } from '../../models';

export interface IDeckImageService {

	/** Gets details about a specified deck image. */
	get(deckId: string, imageId: string): Promise<DeckImage>;

	/** Returns the entire list of deck images for the specified deck */
	list(deckId: string): Promise<DeckImage[]>;

	/** Returns a single page of deck images */
	page(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckImage>>;

	/** Saves the specified image */
	save(deckImage: DeckImage): Promise<DeckImage>;

	/** Deletes the specified image */
	delete(deckId: string, imageId: string): Promise<any>;

	/** Reverts the default image back to a stub image. */
	revert(deckId: string): Promise<any>;

	/** Trims the white space off a default image */
	trimDefault(deckId: string): Promise<any>;

	generatePalette(deckId: string): Promise<DeckColor[]>;

}

@injectable()
export class DeckImageService implements IDeckImageService {

	constructor(
		@inject('IHttpClient') private httpClient: IHttpClient,
		@inject('ILogger') private logger: ILogger,
		@inject('ISettings') private settings: ISettings) {}


	/** Gets details about a specified deck image. */
	public get(deckId: string, imageId: string): Promise<DeckImage> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/images/${imageId}`;
		return this.httpClient.get<DeckImage>(url);
	}

	/** Returns the entire list of deck images for the specified deck */
	public list(deckId: string): Promise<DeckImage[]> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/images`;
		return this.httpClient.get<DeckImage[]>(url);
	}

	/** Returns a single page of deck images */
	public page(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckImage>> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/images/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<DeckImage>>(url);
	}

	/** Saves the specified image */
	public save(deckImage: DeckImage): Promise<DeckImage> {
		let url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckImage.deckId}/images`;
		if (deckImage.id !== null && deckImage.id !== undefined && deckImage.id !== '') {
			url += `/${deckImage.id}`;
			return this.httpClient.put(url, deckImage);
		}
		return this.httpClient.post(url, deckImage);
	}

	/** Deletes the specified image */
	public delete(deckId: string, imageId: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/images/${imageId}`;
		return this.httpClient.delete(url);
	}

	/** Reverts the default image back to a stub image. */
	public revert(deckId: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/images/revert`;
		return this.httpClient.post(url, null);
	}

	/** Trims the white space off a default image */
	public trimDefault(deckId: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/images/trim-default`;
		return this.httpClient.post(url, null);
	}

	public generatePalette(deckId: string): Promise<DeckColor[]> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/images/color-palette`;
		return this.httpClient.post<DeckColor[]>(url, null);
	}

}
